import { UserType } from '@diritto/common/account/constants/user.constant';
import { GlobalNavigationMenu } from '@diritto/common/navigation/constant';
import { Color } from '@diritto/common/style/color';
import { FontWeight, Typography } from '@diritto/common/style/typography';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { isPC, MobileGNBMenuHeight } from 'constants/layout';
import {
  GlobalNavigationMenuHref,
  GlobalNavigationMenuText,
  NewGlobalNavigationMenuDisplayList,
  NewPartnerGlobalNavigationMenuDisplayList,
} from 'constants/navigation';
import { ZIndex } from 'constants/zIndex';
import { useAuthState } from 'contexts/AuthProvider';
import { ExploreIcon } from 'icons/navigation/ExploreIcon';
import { HomeIcon } from 'icons/navigation/HomeIcon';
import { LatestIcon } from 'icons/navigation/LatestIcon';
import { MyIcon } from 'icons/navigation/MyIcon';
import { QuestIcon } from 'icons/navigation/QuestIcon';
import { RankingIcon } from 'icons/navigation/RankingIcon';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'ui-components/common/Link';
import { SafeAreaBottomStyles } from 'ui-components/layout/styles';
import { getDynamicGlobalNavHref } from 'utils/getDynamicGlobalNavHref';

interface MenuProps {
  className?: string;
  selectedMenu?: GlobalNavigationMenu;
}

const IconMap = {
  [GlobalNavigationMenu.Home]: HomeIcon,
  [GlobalNavigationMenu.Explore]: ExploreIcon,
  [GlobalNavigationMenu.Latest]: LatestIcon,
  [GlobalNavigationMenu.Ranking]: RankingIcon,
  [GlobalNavigationMenu.My]: MyIcon,
  [GlobalNavigationMenu.Quest]: QuestIcon,
};

export const MobileGlobalMenu = ({ className, selectedMenu }: MenuProps) => {
  const pathname = usePathname();
  const [hrefMap, setHrefMap] = useState(GlobalNavigationMenuHref);
  const { user } = useAuthState();
  let displayList;
  if (user?.type === UserType.Partner) {
    displayList = NewPartnerGlobalNavigationMenuDisplayList;
  } else {
    displayList = NewGlobalNavigationMenuDisplayList;
  }

  useEffect(() => {
    // React Hydration Error 를 피하기 위해 effect 에서 처리
    const map = NewGlobalNavigationMenuDisplayList.reduce(
      (acc, globalMenu) => ({ ...acc, [globalMenu]: getDynamicGlobalNavHref(globalMenu) }),
      {},
    ) as typeof GlobalNavigationMenuHref;
    setHrefMap(map);
  }, [displayList, pathname]);

  const renderIcon = useCallback(
    (key: GlobalNavigationMenu) => {
      const Icon = IconMap[key] ?? HomeIcon;
      return (
        <Icon
          fill={selectedMenu === key}
          color={selectedMenu === key ? Color.Primary500 : Color.Gray500}
          className="size-[26px]"
        />
      );
    },
    [selectedMenu],
  );

  return (
    <StyledContainer className={className}>
      {displayList.map(key => (
        <StyleLink key={key} href={hrefMap[key]}>
          <StyledMenu isSelected={selectedMenu === key}>
            {renderIcon(key)}
            {GlobalNavigationMenuText[key]}
          </StyledMenu>
        </StyleLink>
      ))}
    </StyledContainer>
  );
};

const StyledContainer = styled.nav`
  position: fixed;
  bottom: 0;
  z-index: ${ZIndex.BottomNavigation};

  width: 100%;

  height: calc(${MobileGNBMenuHeight}px + var(--safe-area-bottom-old));
  height: calc(${MobileGNBMenuHeight}px + var(--safe-area-bottom));

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  background: ${Color.White};
  border-top: 1px solid ${Color.Gray300};

  ${SafeAreaBottomStyles()}

  @media (${isPC}) {
    display: none;
  }
`;

const StyledMenu = styled.p(
  ({ isSelected }: { isSelected?: boolean }) => css`
    cursor: pointer;

    height: 100%;
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    color: ${isSelected ? Color.Primary500 : Color.Gray500};
    ${Typography.XXXSmall};
    font-weight: ${isSelected ? FontWeight.Bold : FontWeight.Medium};
  `,
);

const StyleLink = styled(Link)`
  width: 100%;
`;
